import React, { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import PetronilleModal from '../PetronilleModal';
import ActingOrgBanner from '../ActingOrgBanner';
import DemoCountdownBanner from '../DemoCountdownBanner';
import DemoOnboardingWizard from '../DemoOnboardingWizard';
import DemoBlankWizard from '../DemoBlankWizard';
import { Sparkles, Menu } from 'lucide-react';
import useLiveNotifications from '../../hooks/useLiveNotifications';

const TITLE_MAP = {
  '/app': 'Tableau de bord',
  '/app/sessions': 'Sessions de formation',
  '/app/programmes': 'Catalogue de programmes',
  '/app/stagiaires': 'Apprenants',
  '/app/entreprises': 'Entreprises',
  '/app/formateurs': 'Formateurs',
  '/app/elearning': 'Plateforme e-learning',
  '/app/crm': 'CRM - Pipeline commercial',
  '/app/devis': 'Devis',
  '/app/factures': 'Factures',
  '/app/qualiopi': 'Qualité & Qualiopi',
  '/app/emargements': 'Émargements',
  '/app/evaluations': 'Évaluations apprenants',
  '/app/extranet': 'Extranet clients',
  '/app/parametres': 'Paramètres',
  '/app/profil': 'Mon compte',
};

export default function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [petronille, setPetronille] = useState(false);
  const location = useLocation();
  const title = TITLE_MAP[location.pathname] || '';
  useLiveNotifications(true);

  // Close mobile drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Routes that need the full viewport — no max-width, no padding wrapper.
  // The Email module relies on an Outlook-style 3-pane layout that should
  // fill the available area without any whitespace on the sides.
  const isFullBleed = location.pathname.startsWith('/app/email');

  return (
    <div className="flex h-screen bg-[#F6F7FB] overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div onClick={() => setMobileOpen(false)} className="fixed inset-0 z-40 bg-black/40 lg:hidden" />
          <div className="fixed left-0 top-0 bottom-0 z-50 lg:hidden">
            <Sidebar collapsed={false} onToggle={() => setMobileOpen(false)} />
          </div>
        </>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <DemoCountdownBanner />
        <ActingOrgBanner />
        {/* Mobile hamburger */}
        <div className="lg:hidden flex items-center gap-2 h-14 px-4 bg-white border-b border-[#EFF0F6]">
          <button onClick={() => setMobileOpen(true)} className="w-9 h-9 rounded-lg hover:bg-[#F6F7FB] flex items-center justify-center">
            <Menu className="w-5 h-5 text-[#4A4B63]" />
          </button>
          <span className="font-bold text-[16px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            OF<span className="text-[var(--ofm-primary)]">Manager</span>
          </span>
        </div>

        <Topbar title={title} />
        <main className="flex-1 overflow-hidden flex flex-col">
          {isFullBleed ? (
            <div className="flex-1 min-h-0 overflow-hidden" key={location.pathname}>
              <Outlet />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 md:px-6 py-6 max-w-[1600px] mx-auto slide-up" key={location.pathname}>
                <Outlet />
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Floating OFia button */}
      <button
        onClick={() => setPetronille(true)}
        className="fixed bottom-6 right-6 z-30 w-14 h-14 rounded-2xl ofm-gradient shadow-xl flex items-center justify-center hover:scale-110 transition-transform"
        title="Parler à OFia · Assistante IA"
      >
        <Sparkles className="w-6 h-6 text-white" />
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#14B8A6] border-2 border-white" />
      </button>

      <PetronilleModal open={petronille} onOpenChange={setPetronille} />
      <DemoOnboardingWizard />
      <DemoBlankWizard />
    </div>
  );
}
